import { useEffect, useState, useCallback } from 'react'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useMproStakeRewardContract } from 'hooks/contracts/useMproStakeRewardContract'
import { logMessage } from 'utils/logMessage'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { StakeClaimReward, StakeReward } from './types'
import { useStakeContractAddress } from 'state/global/hooks'
import { BigNumber } from 'ethers'

dayjs.extend(isBetween)

export interface StakePanel {
  stakeConfig: StakeReward
  claimConfig: StakeClaimReward
  rewardConfig?: {
    currentToClaim: BigNumber
  }
}

export const useStakePanel = (
  address?: string
): {
  config: StakePanel | undefined
  isLoading: boolean
  claimStake: () => Promise<void>
  claimReward: () => Promise<void>
  fetchAllData: () => Promise<void>
} => {
  const { account, chainId, isActivating } = useWeb3React()
  const { getConfigWithAccount, getConfigWithoutAccount, claimStake, claimReward } = useMproStakeRewardContract(address)
  const stakeContractAddresses = useStakeContractAddress()
  const [isLoading, setIsLoading] = useState(true)
  const [stakeConfig, setStakeConfig] = useState<StakeReward>()
  const [claimConfig, setClaimConfig] = useState<StakeClaimReward>()
  const [rewardConfig, setRewardClaimConfig] = useState<{ currentToClaim: BigNumber }>()

  const fetchAllData = useCallback(
    async (account?: string): Promise<void> => {
      let config: {
        claimConfig: StakeClaimReward
        stakeConfig: StakeReward
        rewardConfig?: { currentToClaim: BigNumber }
      }
      try {
        setIsLoading(true)
        if (account) {
          config = await getConfigWithAccount(account)
        } else {
          config = await getConfigWithoutAccount()
        }

        setClaimConfig(config.claimConfig)
        setStakeConfig(config.stakeConfig)
        if (config.rewardConfig) {
          setRewardClaimConfig(config.rewardConfig)
        }
      } catch (error) {
        logMessage(`Failed to fetch stake data: ${error}`)
      } finally {
        setIsLoading(false)
      }
    },
    [account, getConfigWithAccount, stakeContractAddresses.length]
  )

  // Scenario wehen thre is Account
  useEffect(() => {
    if (!isActivating && account) {
      fetchAllData(account)
    }
  }, [account, chainId, isActivating, stakeContractAddresses.length])

  // Scenario wehn thre is no account
  useEffect(() => {
    if (!isActivating && !account) {
      fetchAllData()
    }
  }, [chainId, stakeContractAddresses.length, account])

  return {
    config: { stakeConfig, claimConfig, rewardConfig },
    isLoading,
    claimStake,
    fetchAllData,
    claimReward
  }
}

export const formatPrice = (number: number, maxDecimals = 4): string => {
  if (!number || Number.isNaN(number)) return '0'

  // Convert scientific notation to decimal
  const decimalNumber = Number(number.toFixed(20))
  const numberStr = decimalNumber.toString()

  // Handle very small numbers
  if (Math.abs(decimalNumber) < 0.000001 && Math.abs(decimalNumber) > 0) {
    return '<0.000001'
  }

  const [integerPart, decimalPart] = numberStr.split('.')
  const formattedDecimal = decimalPart ? `,${decimalPart.substring(0, maxDecimals)}` : ''
  return `${integerPart}${formattedDecimal}`
}
